import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, LogOut, Shield, CreditCard, LifeBuoy, Package, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { AuthenticatedMobileMenu } from '@/components/navigation/AuthenticatedMobileMenu';
export const AuthHeader = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdminAccess();
  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
  };
  if (!user) {
    return <div className="flex items-center gap-2">
        <Button asChild variant="ghostCopper" className="text-ivory">
          <Link to="/auth">Sign In</Link>
        </Button>
      </div>;
  }
  return <div className="flex items-center gap-3">
      <AuthenticatedMobileMenu />
      <NotificationBell />
      
      {/* Role Badge */}
      {isSuperAdmin && (
        <Badge variant="outline" className="hidden md:inline-flex text-[10px] uppercase tracking-[0.14em] font-medium border-purple-300/50 bg-transparent text-purple-200 gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-purple-300" /> Super Admin
        </Badge>
      )}
      {isAdmin && !isSuperAdmin && (
        <Badge variant="outline" className="hidden md:inline-flex text-[10px] uppercase tracking-[0.14em] font-medium border-copper/50 bg-transparent text-copper gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-copper" /> Admin
        </Badge>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-ivory/10 transition-colors">
            <Avatar className="h-8 w-8 ring-1 ring-ivory/20 hover:ring-copper/60 transition-all">
              <AvatarImage src={user.profile.avatar_url} alt={user.email} />
              <AvatarFallback className="bg-copper/90 text-ivory text-xs font-medium">
                {getInitials(user.profile.first_name, user.profile.last_name)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-60 bg-midnight-green border border-copper/20 shadow-2xl shadow-black/40 z-50 p-1.5" align="end" forceMount side="bottom" sideOffset={8}>
          <DropdownMenuLabel className="font-normal px-2 py-2.5">
            <div className="flex flex-col gap-0.5">
              <p className="font-display text-sm leading-tight text-ivory">
                {user.profile.first_name} {user.profile.last_name}
              </p>
              <p className="font-inter text-xs leading-none text-ivory/55">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-ivory/10" />
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/profile" className="flex items-center">
              <User className="mr-2 h-4 w-4" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/support" className="flex items-center">
              <LifeBuoy className="mr-2 h-4 w-4" />
              Support
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/subscription" className="flex items-center">
              <CreditCard className="mr-2 h-4 w-4" />
              Subscription
            </Link>
          </DropdownMenuItem>
          
          {/* Admin Section */}
          {isAdmin && (
            <>
              <DropdownMenuSeparator className="bg-ivory/10" />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-copper font-medium flex items-center gap-2">
                <Shield className="h-3 w-3" />
                Admin Portal
              </DropdownMenuLabel>
              <DropdownMenuItem asChild className="text-ivory hover:text-red-400 hover:bg-red-500/10 cursor-pointer">
                <Link to="/admin/dashboard" className="flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
            </>
          )}
          
          {/* Super Admin Section */}
          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator className="bg-ivory/10" />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-purple-300 font-medium flex items-center gap-2">
                <Building2 className="h-3 w-3" />
                Enterprise
              </DropdownMenuLabel>
              <DropdownMenuItem asChild className="text-ivory hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer">
                <Link to="/admin/enterprise/overview" className="flex items-center">
                  <Building2 className="mr-2 h-4 w-4" />
                  Enterprise Console
                </Link>
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator className="bg-ivory/10" />
          <DropdownMenuItem className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer" onClick={() => signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};